// src/features/executive/components/KPICard/KPIDetailRow.tsx
import { Box, Tooltip, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { formatVariance } from 'utils/executive.utils'
import { useHero } from '../WidgetCard/HeroContext'

export interface KPIDetailRowProps {
  label: ReactNode
  labelTooltip?: string
  value?: ReactNode
  variance?: number | null
  positiveIsGood?: boolean
  valueAdornment?: ReactNode
}

export default function KPIDetailRow({
  label,
  labelTooltip,
  value,
  variance,
  positiveIsGood = true,
  valueAdornment,
}: KPIDetailRowProps) {
  const { onHero } = useHero()
  const good = variance != null && (positiveIsGood ? variance >= 0 : variance <= 0)

  // On hero, all caption text collapses to white-with-opacity tiers. Plain
  // mode keeps the secondary/success/error theme mapping.
  const labelColor = onHero ? 'rgba(255,255,255,0.85)' : 'text.secondary'
  const valueColor = onHero ? '#FFFFFF' : undefined
  const varianceColor = onHero
    ? 'rgba(255,255,255,0.85)'
    : good
    ? 'success.main'
    : 'error.main'

  const labelNode = (
    <Typography
      variant="caption"
      sx={{
        color: labelColor,
        ...(labelTooltip ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 } : {}),
      }}
    >
      {label}
    </Typography>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1,
        minHeight: 18,
      }}
    >
      {labelTooltip ? (
        <Tooltip
          title={labelTooltip}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
        >
          <Box component="span">{labelNode}</Box>
        </Tooltip>
      ) : (
        labelNode
      )}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        {value != null && (
          <Typography variant="caption" sx={{ fontWeight: 600, color: valueColor }}>
            {value}
          </Typography>
        )}
        {variance != null && (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: varianceColor }}
          >
            {formatVariance(variance)}
          </Typography>
        )}
        {valueAdornment}
      </Box>
    </Box>
  )
}
